import { Note, Project } from '../../../_service/data.service';

type CardSort = {
  project_id: number;
  sort_order: number;
};

/**
 * Pure sorting logic extracted from HomeComponent.
 * Contains stateless functions for managing sort order and sorting notes/projects.
 */
export class SortingLogic {
  /**
   * Assign sort orders to projects based on note modification dates.
   * Projects that haven't been explicitly sorted yet are ordered by when their notes were last modified.
   */
  static assignSortOrderToProjects(projects: Project[] | undefined, noteItems: Note[], card_sort: CardSort[]): void {
    // First, filter out projects that already have sort order assigned in card_sort
    const projectHasSort = new Set(card_sort.map((cs) => cs.project_id));

    // Filter note-items that belong to projects that haven't been sorted yet
    const unsortedNoteItems = noteItems.filter((note) => !projectHasSort.has(note.Project));

    // Sort the note-items by last_modified in descending order (latest first)
    const sortedNoteItems = unsortedNoteItems.sort((a, b) => {
      return new Date(b.last_modified).getTime() - new Date(a.last_modified).getTime();
    });

    // Start assigning sort orders, beginning with the value after the last assigned sort order
    let nextSortValue = card_sort.length + 1;

    // Track projects that have been assigned a sort order in this function call
    const assignedProjects = new Set<number>();

    // Assign sort order based on the sorted note-items
    sortedNoteItems.forEach((note) => {
      const project = (projects ?? []).find((p) => p.ID === note.Project);

      if (project && !projectHasSort.has(project.ID) && !assignedProjects.has(project.ID)) {
        // Add the project to the card_sort with the next sort value
        card_sort.push({ project_id: project.ID, sort_order: nextSortValue });
        nextSortValue++;
        // Mark the project as assigned
        assignedProjects.add(project.ID);
      }
    });

    // Optionally: sort the card_sort array by sort_order if needed
    card_sort.sort((a, b) => a.sort_order - b.sort_order);
  }

  /**
   * Sort notes array based on the card_sort order (projects are ordered)
   */
  static sortNotesByCardSort(filteredNotes: Note[], card_sort: CardSort[]): Note[] {
    // Create a map from project_id to sort_order for quick lookup
    const sortOrderMap = new Map<number, number>();
    card_sort.forEach((card) => {
      sortOrderMap.set(card.project_id, card.sort_order);
    });

    // Sort the data array based on the sort_order in card_sort
    const sorted = [...filteredNotes];
    sorted.sort((a, b) => {
      const sortOrderA = sortOrderMap.get(a.Project) ?? Number.MAX_SAFE_INTEGER;
      const sortOrderB = sortOrderMap.get(b.Project) ?? Number.MAX_SAFE_INTEGER;
      return sortOrderA - sortOrderB;
    });

    return sorted;
  }

  /**
   * Sort projects array based on the card_sort order
   */
  static sortProjectsByCardSort(projects: Project[] | undefined, card_sort: CardSort[]): void {
    if (!projects) return;

    // Create a map from project_id to sort_order for quick lookup
    const sortOrderMap = new Map<number, number>();
    card_sort.forEach((card) => {
      sortOrderMap.set(card.project_id, card.sort_order);
    });

    // Sort the projects array based on the sort_order in card_sort
    projects.sort((a, b) => {
      const sortOrderA = sortOrderMap.get(a.ID) ?? Number.MAX_SAFE_INTEGER;
      const sortOrderB = sortOrderMap.get(b.ID) ?? Number.MAX_SAFE_INTEGER;
      return sortOrderA - sortOrderB;
    });
  }
}
